import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

/* ==============================
   AUTH HEADER HELPER
============================== */
const getAuthHeaders = (token) => ({
  Authorization: `Bearer ${token}`,
  'Content-Type': 'application/json',
});

/* ==============================
   INITIAL STATE
============================== */
const initialState = {
  records: [],
  currentRecord: null,
  isLoading: false,
  error: null,
};

/* ==============================
   ASYNC THUNKS
============================== */

// FETCH ALL INTEGRATED ASSESSMENTS
export const fetchIntegratedAssessments = createAsyncThunk(
  'integratedAssessments/fetchAll',
  async (_, { getState, rejectWithValue }) => {
    try {
      const token = getState().auth.token;
      if (!token) throw 'No auth token';

      const res = await api.get('/integrated-assessments', {
        headers: getAuthHeaders(token),
      });

      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.error || 'Failed to fetch Integrated Assessments'
      );
    }
  }
);

// FETCH SINGLE INTEGRATED ASSESSMENT
export const fetchIntegratedAssessment = createAsyncThunk(
  'integratedAssessments/fetchOne',
  async (id, { getState, rejectWithValue }) => {
    try {
      const token = getState().auth.token;
      if (!token) throw 'No auth token';

      const res = await api.get(`/integrated-assessments/${id}`, {
        headers: getAuthHeaders(token),
      });

      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.error || 'Failed to fetch Integrated Assessment'
      );
    }
  }
);

// FETCH DISCHARGE PLANNING FOR PREFILL (by patient + case) to prefill Discharge Summary
export const fetchIntegratedDischargePlanningByCase = createAsyncThunk(
  'integratedAssessments/dischargePlanningPrefill',
  async ({ patientId, caseId } = {}, { getState, rejectWithValue }) => {
    try {
      const token = getState().auth.token;
      const res = await api.get(`/integrated-assessments/discharge-planning/prefill`, {
        headers: getAuthHeaders(token),
        params: { patient_id: patientId, case_id: caseId },
      });
      return res.data.data; // discharge_planning object or null
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.error || 'Failed to fetch integrated discharge planning'
      );
    }
  }
);

// CREATE INTEGRATED ASSESSMENT
export const createIntegratedAssessment = createAsyncThunk(
  'integratedAssessments/create',
  async (payload, { getState, rejectWithValue }) => {
    try {
      const token = getState().auth.token;
      if (!token) throw 'No auth token';

      const res = await api.post('/integrated-assessments', payload, {
        headers: getAuthHeaders(token),
      });

      return res.data.data;
    } catch (err) {
      if (err.response?.status === 422) {
        return rejectWithValue(err.response.data.errors);
      }

      return rejectWithValue(
        err.response?.data?.error || 'Failed to create Integrated Assessment'
      );
    }
  }
);

// UPDATE INTEGRATED ASSESSMENT (BY ID)
export const updateIntegratedAssessment = createAsyncThunk(
  'integratedAssessments/update',
  async ({ id, payload }, { getState, rejectWithValue }) => {
    try {
      const token = getState().auth.token;
      if (!token) throw 'No auth token';

      const res = await api.put(
        `/integrated-assessments/${id}`,
        payload,
        { headers: getAuthHeaders(token) }
      );

      return res.data.data ?? res.data;
    } catch (err) {
      if (err.response?.status === 422) {
        return rejectWithValue(err.response.data.errors);
      }

      return rejectWithValue(
        err.response?.data?.error || 'Failed to update Integrated Assessment'
      );
    }
  }
);

// DELETE INTEGRATED ASSESSMENT
export const deleteIntegratedAssessment = createAsyncThunk(
  'integratedAssessments/delete',
  async (id, { getState, rejectWithValue }) => {
    try {
      const token = getState().auth.token;
      if (!token) throw 'No auth token';

      await api.delete(`/integrated-assessments/${id}`, {
        headers: getAuthHeaders(token),
      });

      return id;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.error || 'Failed to delete Integrated Assessment'
      );
    }
  }
);

/* ==============================
   SLICE
============================== */
const integratedAssessmentSlice = createSlice({
  name: 'integratedAssessments',
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
    setCurrentRecord(state, action) {
      state.currentRecord = action.payload;
    },
    clearCurrentRecord(state) {
      state.currentRecord = null;
    },
  },
  extraReducers: (builder) => {
    builder

      // FETCH ALL
      .addCase(fetchIntegratedAssessments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchIntegratedAssessments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.records = action.payload;
      })
      .addCase(fetchIntegratedAssessments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // FETCH ONE
      .addCase(fetchIntegratedAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchIntegratedAssessment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentRecord = action.payload;
      })
      .addCase(fetchIntegratedAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // CREATE
      .addCase(createIntegratedAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createIntegratedAssessment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.records.unshift(action.payload);
      })
      .addCase(createIntegratedAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // UPDATE
      .addCase(updateIntegratedAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateIntegratedAssessment.fulfilled, (state, action) => {
        state.isLoading = false;
        const updated = action.payload;

        const index = state.records.findIndex(
          (r) => r.id === updated.id
        );

        if (index !== -1) {
          state.records[index] = updated;
        }

        if (state.currentRecord?.id === updated.id) {
          state.currentRecord = updated;
        }
      })
      .addCase(updateIntegratedAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // DELETE
      .addCase(deleteIntegratedAssessment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteIntegratedAssessment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.records = state.records.filter(
          (r) => r.id !== action.payload
        );

        if (state.currentRecord?.id === action.payload) {
          state.currentRecord = null;
        }
      })
      .addCase(deleteIntegratedAssessment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

/* ==============================
   EXPORTS
============================== */
export const {
  clearError,
  setCurrentRecord,
  clearCurrentRecord,
} = integratedAssessmentSlice.actions;

export default integratedAssessmentSlice.reducer;
